import { autoinject } from "aurelia-framework";
import { Guid } from "../../../../utilities/guid";
import { MenuItem } from "../../../components/menu/menu-item";
import { GenericConfirmationDialogModel } from "../../../../models/basics/generic-confirmation-dialog-model";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { I18N } from "aurelia-i18n";
import { Validations } from "./validations";
import { Permissions } from "./permissions";
import { ManageBranch } from "./manage-branch";
import { ManageDepartment } from "./departments/manage-department";
import { ManageRole } from "./roles/manage-role";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ManageBranchesClientService } from "../../../../services/manage/manage-branches.service";
import { ManageDepartmentsClientService } from "../../../../services/manage/manage-departments.service";
import { ManageRolesClientService } from "../../../../services/manage/manage-roles.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { BranchModel, ManageBranchesPane, ManageBranchesPaneEnum } from "../../../../models/manage/branches.models";
import { DepartmentModel } from "../../../../models/manage/departments.models";
import { RoleModel } from "../../../../models/manage/roles.models";
import { QueryFilter, DataType } from "../../../../models/queries/queries.models";
import * as Enumerable from "linq";
import { PermissionEntity } from "../../../../models/manage/permissions.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BindingSignaler } from "aurelia-templating-resources";
import { BaseClaims } from "../../../../constants/authentication/base-claims";

@autoinject
export class ManageBranches {
    private _paneInDeletion: ManageBranchesPane;

    public canAccessPermissions: boolean;
    public canAccessValidations: boolean;
    public viewModel: ManageBranch | ManageDepartment | ManageRole | Permissions | Validations = null;
    public model: any = null;
    public isChanging: boolean = false;
    public panes: ManageBranchesPane[] = [];
    public scrollElement: HTMLElement;

    constructor(private i18N: I18N,
        private validations: Validations,
        private permissions: Permissions,
        private dialogClientService: DialogClientService,
        private manageBranchesService: ManageBranchesClientService,
        private manageDepartmentsService: ManageDepartmentsClientService,
        private manageRolesService: ManageRolesClientService,
        private notificationService: NotificationClientService,
        private userClientService: UserClientService,
        private manageBranch: ManageBranch,
        private manageDepartment: ManageDepartment,
        private userClaimsService: UserClaimsService,
        private bindingSignaler: BindingSignaler,
        private manageRole: ManageRole) {
        this.canAccessPermissions = this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions);
        this.canAccessValidations = this.userClaimsService.hasPermission(BaseClaims.CanAccessValidations);
    }

    public getMenuItems(pane: ManageBranchesPane, item: BranchModel | DepartmentModel | RoleModel): MenuItem[] {
        let id = 0;
        let permissionEntity = null;
        let name = "";

        if (pane.type == ManageBranchesPaneEnum.Subbranch) {
            id = (<BranchModel>item).branchId;
            permissionEntity = PermissionEntity.Branch;
            name = (<BranchModel>item).name;
        }

        if (pane.type == ManageBranchesPaneEnum.Branch) {
            id = (<BranchModel>item).branchId;
            permissionEntity = PermissionEntity.Branch;
            name = (<BranchModel>item).name;
        }

        if (pane.type == ManageBranchesPaneEnum.Department) {
            id = (<DepartmentModel>item).departmentId;
            permissionEntity = PermissionEntity.Department;
            name = (<DepartmentModel>item).name;
        }

        if (pane.type == ManageBranchesPaneEnum.Role) {
            id = (<RoleModel>item).roleId;
            permissionEntity = PermissionEntity.Role;
            name = (<RoleModel>item).roleName;
        }

        return [
            {
                item: {
                    icon: 'security',
                    title: this.i18N.tr('permissions-text')
                },
                isVisible: this.canAccessPermissions,
                click: () => {
                    if (!this.canAccessPermissions) return;

                    this.canExitVM().then(() => {
                        const childPane = Enumerable.from(this.panes).firstOrDefault(x => x.parentId == pane.id);
                        if (childPane) {
                            this.closePaneAndChildren(childPane);
                        }

                        const newPane = {
                            title: `${name} ${this.i18N.tr('permissions-text')}`,
                            type: ManageBranchesPaneEnum.Compose,
                            id: Guid.generateGuid(),
                            parentId: pane.id,
                            showCloseButton: true
                        };

                        this.viewModel = this.permissions;
                        this.model = {
                            id: id,
                            type: permissionEntity,
                            showCloseButton: true,
                            afterSaveOperation: () => {
                                const canAccessPermissions = this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions);
                                const canAccessValidations = this.userClaimsService.hasPermission(BaseClaims.CanAccessValidations);
                                const refreshMenuItems = this.canAccessPermissions != canAccessPermissions || this.canAccessValidations != canAccessValidations;
                                this.canAccessPermissions = canAccessPermissions;
                                this.canAccessValidations = canAccessValidations;

                                if (refreshMenuItems) {
                                    this.bindingSignaler.signal('update-menu-items');
                                    this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Compose);
                                    this.viewModel = null;
                                    this.model = null;
                                }
                            },
                            cancelOperation: () => this.initializeCompose()
                        }

                        this.panes.push(newPane);
                        this.scrollToEnd();
                    })
                },
                classList: ''
            },
            {
                item: {
                    icon: 'warning',
                    title: this.i18N.tr('validations-text')
                },
                isVisible: this.canAccessValidations,
                click: () => {
                    if (!this.canAccessValidations) return;

                    this.canExitVM().then(() => {
                        this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Compose);
                        this.viewModel = this.validations;
                        this.model = {
                            type: permissionEntity,
                            id: id,
                            showCloseButton: true,
                            usePassedCancelOperation: true,
                            cancelOperation: () => this.initializeCompose()
                        }

                        const newPane = {
                            title: `${name} ${this.i18N.tr('validations-text')}`,
                            type: ManageBranchesPaneEnum.Compose,
                            id: Guid.generateGuid(),
                            parentId: pane.id,
                            showCloseButton: true
                        };

                        this.panes.push(newPane);
                        this.scrollToEnd();
                    })
                },
                classList: ''
            },
            {
                item: {
                    icon: item.isActive ? 'close' : 'done',
                    title: item.isActive ? this.i18N.tr('deactivate-text') : this.i18N.tr('activate-text')
                },
                isVisible: true,
                click: () => this.changeItemActivationState(pane, item),
                classList: ''
            },
            {
                item: {
                    icon: 'filter_none',
                    title: this.i18N.tr('clone-text')
                },
                isVisible: true,
                click: () => this.showCloneDialog(pane, item),
                classList: ''
            },
            {
                item: {
                    icon: 'delete',
                    title: this.i18N.tr('delete-text')
                },
                isVisible: true,
                click: () => this.showDeleteDialog(pane, item),
                classList: ''
            }
        ];
    }

    public getPaneType(pane: ManageBranchesPane) {
        if (pane.type == ManageBranchesPaneEnum.Branch || pane.type == ManageBranchesPaneEnum.Subbranch) {
            return "branch";
        }

        if (pane.type == ManageBranchesPaneEnum.Department) {
            return "department";
        }

        if (pane.type == ManageBranchesPaneEnum.Role) {
            return "role";
        }

        if (pane.type == ManageBranchesPaneEnum.Compose) {
            return "compose";
        }
    }

    public onItemSelected(pane: ManageBranchesPane, $event) {
        if (pane.type == ManageBranchesPaneEnum.Role) return;

        this.canExitVM().then(() => {
            const item = $event.detail.item;
            pane.selectedItem = item;

            const childPane = Enumerable.from(this.panes).firstOrDefault(x => x.parentId == pane.id);
            if (childPane) {
                this.closePaneAndChildren(childPane);
            }
            this.viewModel = null;
            this.model = null;
            let newPane = null;

            if (pane.type == ManageBranchesPaneEnum.Branch || pane.type == ManageBranchesPaneEnum.Subbranch) {
                const initialFilter = QueryFilter.getQuery('branchId', (<BranchModel>item).branchId, "eq", DataType.Number, false);

                newPane = {
                    title: `${item.name} ${this.i18N.tr('departments-text')}`,
                    type: ManageBranchesPaneEnum.Department,
                    initialFilters: [initialFilter],
                    searchFilters: ["name", "scope"],
                    uri: "Departments",
                    selectedItem: null,
                    id: Guid.generateGuid(),
                    fallbackText: this.i18N.tr('departments-fallback-text'),
                    parentId: pane.id,
                    showCloseButton: true
                };
            }

            if (pane.type == ManageBranchesPaneEnum.Department) {
                const initialFilter = QueryFilter.getQuery('departmentId', (<DepartmentModel>item).departmentId, "eq", DataType.Number, false);

                newPane = {
                    title: `${item.name} ${this.i18N.tr('roles-text')}`,
                    type: ManageBranchesPaneEnum.Role,
                    initialFilters: [initialFilter],
                    searchFilters: ["roleName"],
                    uri: "Roles",
                    fallbackText: this.i18N.tr('roles-fallback-text'),
                    selectedItem: null,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                };
            }

            this.panes.push(newPane);
            this.scrollToEnd();
        })
    }

    public addItem(pane: ManageBranchesPane) {
        this.canExitVM().then(() => {
            pane.listVM.selectedItem = null;
            pane.selectedItem = null;
            const childPane = Enumerable.from(this.panes).firstOrDefault(x => x.parentId == pane.id);
            if (childPane) {
                this.closePaneAndChildren(childPane);
            }
            const parentPane = this.panes.filter(x => x.id == pane.parentId)[0];
            this.viewModel = null;
            this.model = null;
            let newPane = null;

            const saveOperation = (newPane) => {
                this.reload(pane);
                this.viewModel = null;
                this.model = null;
                this.closePaneAndChildren(newPane);
            }

            const closeOperation = (newPane) => {
                this.canExitVM().then(() => {
                    this.viewModel = null;
                    this.model = null;
                    this.closePaneAndChildren(newPane);
                })
            }

            if (pane.type == ManageBranchesPaneEnum.Branch || pane.type == ManageBranchesPaneEnum.Subbranch) {
                const title: string = pane.type == ManageBranchesPaneEnum.Branch ? this.i18N.tr('add-branch') : this.i18N.tr('add-sub-branch-text');
                newPane = {
                    title: title,
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                };

                this.viewModel = this.manageBranch;
                this.model = {
                    cancelEditing: () => closeOperation(newPane),
                    refresh: () => saveOperation(newPane),
                    parentBranchId: parentPane == null ? null : (<BranchModel>parentPane.selectedItem).branchId,
                    branchId: 0,
                    title: title,
                }
            }

            if (pane.type == ManageBranchesPaneEnum.Department) {
                newPane = {
                    title: this.i18N.tr('add-department'),
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                }

                const selectedBranch = <BranchModel>parentPane.selectedItem;

                this.viewModel = this.manageDepartment;
                this.model = {
                    branchId: selectedBranch.branchId,
                    departmentId: 0,
                    cancelOperation: () => closeOperation(newPane),
                    saveOperation: () => saveOperation(newPane),
                    title: this.i18N.tr('add-department'),
                };
            }

            if (pane.type == ManageBranchesPaneEnum.Role) {
                newPane = {
                    title: this.i18N.tr('add-role'),
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                }

                const selectedDepartment = <DepartmentModel>parentPane.selectedItem;

                this.viewModel = this.manageRole;
                this.model = {
                    departmentId: selectedDepartment.departmentId,
                    roleId: 0,
                    saveOperation: () => saveOperation(newPane),
                    cancelOperation: () => closeOperation(newPane),
                    title: this.i18N.tr('add-role'),
                }
            }

            this.panes.push(newPane)
            this.scrollToEnd();
        });
    }

    public editItem(event, pane: ManageBranchesPane, item: BranchModel | DepartmentModel | RoleModel) {
        if (event) {
            event.stopPropagation();
        }

        this.canExitVM().then(() => {
            pane.listVM.selectedItem = item;
            const childPane = Enumerable.from(this.panes).firstOrDefault(x => x.parentId == pane.id);
            if (childPane) {
                this.closePaneAndChildren(childPane);
            }
            let newPane = null;

            const cancelOperation = (newPane) => {
                this.canExitVM().then(() => {
                    this.viewModel = null;
                    this.model = null;
                    this.closePaneAndChildren(newPane);
                })
            }

            const saveOperation = (newPane) => {
                this.reload(pane);
                this.viewModel = null;
                this.model = null;
                this.closePaneAndChildren(newPane);
            }

            if (pane.type == ManageBranchesPaneEnum.Branch || pane.type == ManageBranchesPaneEnum.Subbranch) {
                newPane = {
                    title: `${this.i18N.tr('editing-branch-text')} ${(<BranchModel>item).name}`,
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                };

                this.viewModel = this.manageBranch;
                this.model = {
                    cancelEditing: () => cancelOperation(newPane),
                    refresh: () => saveOperation(newPane),
                    parentBranchId: (<BranchModel>item).parentId,
                    branchId: (<BranchModel>item).branchId,
                    title: `${this.i18N.tr('editing-branch-text')} ${(<BranchModel>item).name}`,
                }
            }

            if (pane.type == ManageBranchesPaneEnum.Department) {
                newPane = {
                    title: `${this.i18N.tr('editing-department-text')} ${(<DepartmentModel>item).name}`,
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                }

                this.viewModel = this.manageDepartment;
                this.model = {
                    branchId: (<DepartmentModel>item).branchId,
                    departmentId: (<DepartmentModel>item).departmentId,
                    cancelOperation: () => cancelOperation(newPane),
                    saveOperation: () => saveOperation(newPane),
                    title: `${this.i18N.tr('editing-department-text')} ${(<DepartmentModel>item).name}`
                };
            }

            if (pane.type == ManageBranchesPaneEnum.Role) {
                newPane = {
                    title: `${this.i18N.tr('editing-role-text')} ${(<RoleModel>item).roleName}`,
                    type: ManageBranchesPaneEnum.Compose,
                    id: Guid.generateGuid(),
                    parentId: pane.id,
                    showCloseButton: true
                }
                this.viewModel = this.manageRole;
                this.model = {
                    departmentId: (<RoleModel>item).departmentId,
                    roleId: (<RoleModel>item).roleId,
                    saveOperation: () => saveOperation(newPane),
                    cancelOperation: () => cancelOperation(newPane),
                    title: `${this.i18N.tr('editing-role-text')} ${(<RoleModel>item).roleName}`
                }
            }

            this.panes.push(newPane);
            this.scrollToEnd();
        })
    }

    public showDeleteDialog(pane: ManageBranchesPane, item: BranchModel | DepartmentModel | RoleModel) {
        this._paneInDeletion = pane;
        this.dialogClientService.show(DialogServiceConstants.URLs.DeleteModal, {
            delete: () => this.delete(item)
        });
    }

    public delete(item: BranchModel | DepartmentModel | RoleModel) {
        const thenOperation = (message) => {
            this.dialogClientService.hide();
            this.notificationService.show(this.i18N.tr('successfully-deleted-text'), message, NotificationType.Success);
            if (item == this._paneInDeletion.selectedItem) {
                this.closePaneChildren(this._paneInDeletion);
            }
            this.reload(this._paneInDeletion);
            this._paneInDeletion = null;
        }

        const catchOperation = (message) => {
            this.notificationService.show(this.i18N.tr('warning') + "!", message, NotificationType.Warning);
        }

        if ('branchId' in item && (this._paneInDeletion.type == ManageBranchesPaneEnum.Branch ||
            this._paneInDeletion.type == ManageBranchesPaneEnum.Subbranch)) {
            this.manageBranchesService.deleteBranch({ branchIds: [item.branchId] }).then(() => thenOperation(this.i18N.tr('branch-has-been-successfully-deleted-text'))).catch(() => catchOperation(this.i18N.tr('branch-cannot-be-deleted-in-use-text')));
        }

        if ('departmentId' in item && this._paneInDeletion.type == ManageBranchesPaneEnum.Department) {
            this.manageDepartmentsService.deleteDepartment({ departmentIds: [item.departmentId] }).then(() => thenOperation(this.i18N.tr('department-has-been-successfully-deleted-text'))).catch(() => catchOperation(this.i18N.tr('department-cannot-be-deleted-in-use-text')));
        }

        if ('roleId' in item && this._paneInDeletion.type == ManageBranchesPaneEnum.Role) {
            this.manageRolesService.deleteRole({ roleId: item.roleId }).then(() => thenOperation(this.i18N.tr('role-has-been-successfully-deleted-text'))).catch(() => catchOperation(this.i18N.tr('role-cannot-be-deleted-in-use-text')));
        }
    }

    public showCloneDialog(pane: ManageBranchesPane, item: BranchModel | DepartmentModel | RoleModel) {
        let cloneType = "";

        if (pane.type == ManageBranchesPaneEnum.Branch) {
            cloneType = "branch";
        }

        if (pane.type == ManageBranchesPaneEnum.Department) {
            cloneType = "department";
        }

        if (pane.type == ManageBranchesPaneEnum.Role) {
            cloneType = "role";
        }

        this.dialogClientService.show(DialogServiceConstants.URLs.CloneDialog, {
            item: item,
            reload: () => this.reload(pane),
            cloneType: cloneType
        });
    }

    public openSubBranches(event, pane: ManageBranchesPane, branch: BranchModel) {
        if (event) {
            event.stopPropagation();
            pane.selectedItem = branch;
            pane.listVM.selectedItem = branch;
            const subbranch = this.panes.filter(x => x.parentId == pane.id)[0];
            if (subbranch) {
                this.closePaneAndChildren(subbranch);
            }
            this.viewModel = null;
            this.model = null;

            const initialFilter = QueryFilter.getQuery('parentId', branch.branchId, "eq", DataType.Number, false, false, null, false, false);

            const newPane = {
                title: `${branch.name} ${this.i18N.tr('sub-branches-text')}`,
                type: ManageBranchesPaneEnum.Subbranch,
                initialFilters: [initialFilter],
                searchFilters: ["name", "code"],
                uri: "Branches",
                selectedItem: null,
                id: Guid.generateGuid(),
                parentId: pane.id,
                fallbackText: this.i18N.tr('branches-fallback-text'),
                showCloseButton: true
            };

            this.panes.push(newPane);
            this.scrollToEnd();
        }
    }

    public changeItemActivationState(pane: ManageBranchesPane, item: BranchModel | DepartmentModel | RoleModel, authorizedDeactivation: boolean = false) {
        if (this.isChanging) {
            return;
        }

        this.isChanging = true;

        const thenOperation = x => {
            if (x.isSuccess) {
                let message = "";
                if (item.isActive) {
                    message = this.i18N.tr('successful-deactivation-text');
                }
                else {
                    message = this.i18N.tr('successful-activation-text');
                }

                this.userClientService.reloadUser();
                this.notificationService.show(message, "", NotificationType.Success);
                this.reload(pane);
            }
            else if (x.unauthorizedDeactivation) {
                const model = new GenericConfirmationDialogModel();
                model.confirm = this.i18N.tr('deactivate-text');
                model.cancel = this.i18N.tr('cancel-text');

                if (pane.type == ManageBranchesPaneEnum.Branch) {
                    model.messages.push(this.i18N.tr('are-you-sure-deactivate-branch'));
                }
                else if (pane.type == ManageBranchesPaneEnum.Department) {
                    model.messages.push(this.i18N.tr('are-you-sure-deactivate-department'));
                }
                else if (pane.type == ManageBranchesPaneEnum.Role) {
                    model.messages.push(this.i18N.tr('are-you-sure-deactivate-role'));
                }

                model.title = this.i18N.tr('confirm-deactivation');
                model.onConfirm = () => this.changeItemActivationState(pane, item, true);
                this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
            }
            else {
                this.notificationService.show(this.i18N.tr('branch-not-found-text'), "", NotificationType.Warning, 5000);
            }

            this.isChanging = false;
        };

        const catchOperation = () => {
            this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error, 5000);
            this.isChanging = false;
        }

        if (pane.type == ManageBranchesPaneEnum.Branch || pane.type == ManageBranchesPaneEnum.Subbranch) {
            this.manageBranchesService.changeBranchActivationState({ id: (<BranchModel>item).branchId, authorizeDeactivation: authorizedDeactivation }).then(thenOperation).catch(catchOperation)
        }

        if (pane.type == ManageBranchesPaneEnum.Department) {
            this.manageDepartmentsService.changeDepartmentActivationState({ id: (<DepartmentModel>item).departmentId, authorizeDeactivation: authorizedDeactivation }).then(thenOperation).catch(catchOperation);
        }

        if (pane.type == ManageBranchesPaneEnum.Role) {
            this.manageRolesService.changeRoleActivationState({ id: (<RoleModel>item).roleId, authorizeDeactivation: authorizedDeactivation }).then(thenOperation).catch(catchOperation);
        }
    }

    private reload(pane: ManageBranchesPane) {
        pane.listVM.loadItems().then(() => pane.selectedItem = pane.listVM.selectedItem);
    }

    private scrollToEnd() {
        //setTimeout needed as otherwise component is still hidden by aurelia
        setTimeout(() => {
            $(this.scrollElement).stop();
            $(this.scrollElement).animate({ scrollLeft: this.scrollElement.scrollWidth }, 800)
        });
    }

    private initializeCompose() {
        this.canExitVM().then(x => {
            if (x) {
                this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Compose);
                this.viewModel = null;
                this.model = null;
            }
        })
    }

    private canExitVM() {
        return new Promise<any>((resolve, reject) => {
            if (this.viewModel && this.viewModel.areThereUnsavedChanges()) {
                const model = new GenericConfirmationDialogModel();
                model.confirm = this.i18N.tr("yes-text");
                model.cancel = this.i18N.tr("cancel-text");
                model.messages.push(this.i18N.tr("unsaved-changes-text"));
                model.title = this.i18N.tr("discard-unsaved-changes-text");
                model.onConfirm = () => resolve(true);
                model.onCancel = () => reject(false);

                this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
            }
            else {
                resolve(true);
            }
        });
    }

    public closeList(pane: ManageBranchesPane) {
        this.canExitVM()
            .then(() => {
                if (pane.type == ManageBranchesPaneEnum.Subbranch) {
                    this.closePaneAndChildren(pane);
                }

                if (pane.type == ManageBranchesPaneEnum.Department) {
                    this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Department);
                }

                if (pane.type == ManageBranchesPaneEnum.Role || pane.type == ManageBranchesPaneEnum.Department) {
                    this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Role);
                }

                if (pane.type == ManageBranchesPaneEnum.Compose || pane.type == ManageBranchesPaneEnum.Role || pane.type == ManageBranchesPaneEnum.Department) {
                    this.panes = this.panes.filter(x => x.type != ManageBranchesPaneEnum.Compose);
                    this.viewModel = null;
                    this.model = null;
                }
            })
    }

    private closePaneAndChildren(pane: ManageBranchesPane) {
        const paneId = pane.id;
        this.closePaneChildren(pane);
        this.panes = this.panes.filter(x => x.id != paneId);
    }

    private closePaneChildren(pane: ManageBranchesPane) {
        let paneId = pane.id;
        let index = 0;
        while (index != -1) {
            index = this.panes.findIndex(x => x.parentId == paneId);

            if (index != -1) {
                paneId = this.panes[index].id;
                this.panes.splice(index, 1);
            }
        }
    }

    activate() {
        const initialFilter = QueryFilter.getQuery('parentId', null, "eq", DataType.Number, false, false, null, false, false);

        this.panes.push({
            title: this.i18N.tr('branches-text'),
            type: ManageBranchesPaneEnum.Branch,
            initialFilters: [initialFilter],
            searchFilters: ["name", "code"],
            uri: "Branches",
            fallbackText: this.i18N.tr('branches-fallback-text'),
            selectedItem: null,
            id: Guid.generateGuid(),
            parentId: '0',
            showCloseButton: false
        })
    }
}
